import React, { createContext, useContext, useState, useEffect } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';

const AuthContext = createContext();

// Fallback to original admin UUID if settings don't exist
const FALLBACK_ADMIN_UID = 'sZQ7z2w0IIf14AQAEQgKqgaqrB32';

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [uuidsLoaded, setUuidsLoaded] = useState(false);
  const [authResolved, setAuthResolved] = useState(false);
  const [adminUUIDs, setAdminUUIDs] = useState([FALLBACK_ADMIN_UID]);

  // Load admin UUIDs from Firestore
  useEffect(() => {
    const applyAdminUUIDs = (uuids) => {
      setAdminUUIDs(Array.isArray(uuids) && uuids.length > 0 ? uuids : [FALLBACK_ADMIN_UID]);
      setUuidsLoaded(true);
    };

    const loadAdminUUIDs = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'settings', 'global'));
        if (settingsDoc.exists()) {
          applyAdminUUIDs(settingsDoc.data().adminUUIDs || []);
        } else {
          applyAdminUUIDs([FALLBACK_ADMIN_UID]);
        }
      } catch (err) {
        console.error('Error loading admin UUIDs:', err);
        applyAdminUUIDs([FALLBACK_ADMIN_UID]);
      }
    };

    loadAdminUUIDs();

    const unsubscribe = onSnapshot(doc(db, 'settings', 'global'), (snapshot) => {
      if (snapshot.exists()) {
        applyAdminUUIDs(snapshot.data().adminUUIDs || []);
      } else {
        applyAdminUUIDs([FALLBACK_ADMIN_UID]);
      }
    }, () => {
      applyAdminUUIDs([FALLBACK_ADMIN_UID]);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!uuidsLoaded) return undefined;
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user && adminUUIDs.includes(user.uid)) {
        setIsAdmin(true);
        localStorage.setItem('adminAuthenticated', 'true');
      } else {
        setIsAdmin(false);
        localStorage.removeItem('adminAuthenticated');
        if (user && !adminUUIDs.includes(user.uid)) {
          signOut(auth).catch(err => console.error('Error signing out removed admin:', err));
        }
      }
      setAuthResolved(true);
    });

    return () => unsubscribe();
  }, [adminUUIDs, uuidsLoaded]);

  const logout = async () => {
    try {
      await signOut(auth);
      setIsAdmin(false);
      localStorage.removeItem('adminAuthenticated');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  return (
    <AuthContext.Provider value={{ isAdmin, loading: !uuidsLoaded || !authResolved, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

